/**
 * useFocusTrap Hook
 * 
 * Traps focus within a container element for accessibility.
 * Used primarily for modals and dialogs to ensure keyboard users
 * cannot tab out of the modal while it's open.
 * 
 * Features:
 * - Traps focus within container
 * - Returns focus to trigger element on close
 * - Handles Escape key to close
 * - Auto-focuses first focusable element
 */

import { useEffect, useRef, useCallback } from 'react'

const FOCUSABLE_SELECTORS = [
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'a[href]',
  '[tabindex]:not([tabindex="-1"])',
  '[contenteditable="true"]',
].join(', ')

/**
 * Open traps, oldest first. When dialogs stack (the "Confirm it's you" prompt over a busy
 * confirmation modal), only the topmost may act on keys: a trap underneath used to swallow every
 * Tab (it had no enabled buttons) or drag focus back into itself.
 */
const openTraps: symbol[] = []

interface UseFocusTrapOptions {
  /** Whether the focus trap is active */
  isActive: boolean
  /** Callback when Escape key is pressed */
  onEscape?: () => void
  /** Whether to auto-focus first focusable element */
  autoFocus?: boolean
  /** Whether to return focus to trigger element on close */
  returnFocus?: boolean
}

interface UseFocusTrapReturn {
  /** Ref to attach to the container element */
  containerRef: React.RefObject<HTMLDivElement | null>
  /** Ref to the element that triggered the trap (for returning focus) */
  triggerRef: React.RefObject<HTMLElement | null>
}

export function useFocusTrap(options: UseFocusTrapOptions): UseFocusTrapReturn {
  const { 
    isActive, 
    onEscape, 
    autoFocus = true, 
    returnFocus = true 
  } = options

  const containerRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLElement>(null)
  const trapId = useRef(Symbol('focus-trap'))
  const previousActiveElement = useRef<HTMLElement | null>(null)

  // Get all focusable elements within container
  const getFocusableElements = useCallback((): HTMLElement[] => {
    if (!containerRef.current) return []
    
    const elements = containerRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTORS)
    return Array.from(elements).filter(
      (el) => el.offsetParent !== null // Exclude hidden elements
    )
  }, [])

  // Handle keyboard navigation
  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    if (!isActive || !containerRef.current) return
    if (openTraps[openTraps.length - 1] !== trapId.current) return

    // Handle Escape key
    if (event.key === 'Escape' && onEscape) {
      event.preventDefault()
      event.stopPropagation()
      onEscape()
      return
    }

    // Handle Tab key for focus trapping
    if (event.key === 'Tab') {
      const focusableElements = getFocusableElements()
      
      if (focusableElements.length === 0) {
        event.preventDefault()
        return
      }

      const firstElement = focusableElements[0]
      const lastElement = focusableElements[focusableElements.length - 1]
      const activeElement = document.activeElement as HTMLElement

      // Shift + Tab: Move to last element if on first
      if (event.shiftKey && activeElement === firstElement) {
        event.preventDefault()
        lastElement.focus()
        return
      }

      // Tab: Move to first element if on last
      if (!event.shiftKey && activeElement === lastElement) {
        event.preventDefault()
        firstElement.focus()
        return
      }

      // If focus is outside container, bring it back
      if (!containerRef.current.contains(activeElement)) {
        event.preventDefault()
        firstElement.focus()
      }
    }
  }, [isActive, onEscape, getFocusableElements])

  // Store active element when trap is activated
  useEffect(() => {
    if (!isActive) return

    // Store the currently focused element to return focus later
    if (!previousActiveElement.current) {
      previousActiveElement.current = document.activeElement as HTMLElement
      
      // Store it in triggerRef for external access
      if (previousActiveElement.current) {
        (triggerRef as React.MutableRefObject<HTMLElement | null>).current = previousActiveElement.current
      }
    }
  }, [isActive])

  // Auto-focus first focusable element
  useEffect(() => {
    if (!isActive || !autoFocus) return

    // Small delay to ensure modal is rendered
    const timeoutId = setTimeout(() => {
      const focusableElements = getFocusableElements()
      if (focusableElements.length > 0) {
        focusableElements[0].focus()
      } else if (containerRef.current) {
        // If no focusable elements, focus the container itself
        containerRef.current.setAttribute('tabindex', '-1')
        containerRef.current.focus()
      }
    }, 10)

    return () => clearTimeout(timeoutId)
  }, [isActive, autoFocus, getFocusableElements])

  // Register as the topmost trap while active
  useEffect(() => {
    if (!isActive) return
    const id = trapId.current
    openTraps.push(id)
    return () => {
      const index = openTraps.lastIndexOf(id)
      if (index !== -1) openTraps.splice(index, 1)
    }
  }, [isActive])

  // Add keyboard event listener
  useEffect(() => {
    if (!isActive) return

    document.addEventListener('keydown', handleKeyDown)
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isActive, handleKeyDown])

  // Return focus when deactivated
  useEffect(() => {
    if (isActive) return

    // Return focus to the previously focused element
    if (returnFocus && previousActiveElement.current) {
      // Small delay to ensure we're returning focus after modal is fully closed
      const timeoutId = setTimeout(() => {
        if (previousActiveElement.current && document.body.contains(previousActiveElement.current)) {
          previousActiveElement.current.focus()
        }
        previousActiveElement.current = null
      }, 10)

      return () => clearTimeout(timeoutId)
    }
  }, [isActive, returnFocus])

  // Prevent scrolling on body when trap is active
  useEffect(() => {
    if (!isActive) return

    const originalOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    return () => {
      document.body.style.overflow = originalOverflow
    }
  }, [isActive])

  return {
    containerRef,
    triggerRef,
  }
}

export default useFocusTrap
